/**
 * Canal de envio: fallbacks WhatsApp na ordem de uso.
 */
import React, { useState } from 'react';
import Select from '../../../../components/ui/Select';
import { Button } from '../../../../components/ui/Button';
import { AppIcon } from '../../../../components/icons/AppIcon';
import type { WhatsAppConnectionOption } from '../../auto-movement/hooks/useWhatsAppConnections';

interface FollowUpChannelFieldsProps {
    connections: WhatsAppConnectionOption[];
    loadingConnections: boolean;
    backupConnectionIds: string[];
    onAdd: (connectionId: string) => void;
    onRemove: (connectionId: string) => void;
}

export default function FollowUpChannelFields({
    connections,
    loadingConnections,
    backupConnectionIds,
    onAdd,
    onRemove,
}: FollowUpChannelFieldsProps) {
    const [selectedConnectionId, setSelectedConnectionId] = useState('');

    const connectionLabel = (connectionId: string) => {
        const connection = connections.find((item) => item.id === connectionId);
        return connection?.phone_number || connection?.instance_name || connectionId;
    };

    const availableConnections = connections.filter(
        (connection) => !backupConnectionIds.includes(connection.id),
    );

    const handleAdd = () => {
        if (!selectedConnectionId) return;
        onAdd(selectedConnectionId);
        setSelectedConnectionId('');
    };

    return (
        <section className="rounded-xl border border-border bg-surface p-s4 md:p-s5" aria-labelledby="followup-channel">
            <h2 id="followup-channel" className="text-sm font-semibold text-default">
                Canal de envio
            </h2>
            <p className="mt-s1 text-sm text-muted">
                Se o canal da conversa estiver indisponível, o sistema usa os canais abaixo na
                ordem definida.
            </p>

            <div className="mt-s4 flex flex-col gap-s2 sm:flex-row sm:items-stretch">
                <Select
                    value={selectedConnectionId}
                    onChange={(event) => setSelectedConnectionId(event.target.value)}
                    placeholder={loadingConnections ? 'Carregando canais...' : 'Selecione um canal'}
                    disabled={loadingConnections || availableConnections.length === 0}
                    options={availableConnections.map((connection) => ({
                        value: connection.id,
                        label: connection.phone_number || connection.instance_name,
                    }))}
                    containerClassName="min-w-0 flex-1"
                    aria-label="Selecione um canal"
                />
                <Button
                    type="button"
                    onClick={handleAdd}
                    disabled={!selectedConnectionId}
                    className="min-h-11 shrink-0 touch-manipulation sm:min-h-9"
                >
                    Conectar
                </Button>
            </div>

            {connections.length === 0 && !loadingConnections && (
                <p className="mt-s3 rounded-lg border border-warning/20 bg-warning-bg px-s3 py-s2 text-sm text-warning-text">
                    Nenhum canal conectado. Conecte um canal em <strong>Configurações → Canais</strong>.
                </p>
            )}

            {backupConnectionIds.length > 0 && (
                <ol className="mt-s4 flex flex-col gap-s2">
                    {backupConnectionIds.map((connectionId, index) => (
                        <li
                            key={connectionId}
                            className="flex items-center justify-between gap-s3 rounded-xl border border-border bg-surface-subtle px-s3 py-s2"
                        >
                            <span className="flex min-w-0 items-center gap-s2 text-sm text-default">
                                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-subtle text-xs font-semibold text-primary">
                                    {index + 1}
                                </span>
                                <span className="truncate">{connectionLabel(connectionId)}</span>
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => onRemove(connectionId)}
                                aria-label={`Remover canal ${connectionLabel(connectionId)}`}
                                className="min-h-11 min-w-11 touch-manipulation text-error hover:text-error md:min-h-8 md:min-w-8"
                            >
                                <AppIcon name="trash" className="size-4" aria-hidden />
                            </Button>
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
}
